import React from 'react';
import { Avatar, Box, Drawer, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import './UserProfileMobileDrawer.styles.scss';
import { Link } from 'react-router-dom';
import { userDisplayName } from '../../utils/user';
import useTranslation from '../../translations/use-translation';

const drawerRootSx = {
  '& .MuiDrawer-paper': {
    width: { xs: '300px', sm: '320px' },
    maxWidth: '88vw',
    boxSizing: 'border-box',
    border: 'none',
  },
};

const drawerBackdropSx = {
  backgroundColor: 'rgba(0,0,0,0.45)',
  backdropFilter: 'blur(4px)',
};

const OnlineBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: '0 0 0 2px #ffffff',
    width: 12,
    height: 12,
    borderRadius: '50%',
    minWidth: 'unset',
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.8s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%':   { transform: 'scale(.8)', opacity: 1 },
    '100%': { transform: 'scale(2.4)', opacity: 0 },
  },
}));

const UserProfileMobileDrawer = ({
  toggleUserProfileMobileDrawer,
  userInfo,
  signOut,
  setToggleUserProfileMobileDrawer,
}) => {
  const translate = useTranslation();
  const handleClose = () => setToggleUserProfileMobileDrawer(false);
  const profileImageLetters = userDisplayName(userInfo?.displayName);
  const name = userInfo?.displayName ? userInfo.displayName.split(' ').join('') : '';

  return (
    <Drawer
      transitionDuration={300}
      elevation={0}
      anchor="right"
      open={toggleUserProfileMobileDrawer}
      variant="temporary"
      onClose={handleClose}
      ModalProps={{
        keepMounted: true,
        BackdropProps: { sx: drawerBackdropSx },
      }}
      sx={drawerRootSx}
    >
      <Box className="upd">

        {/* ── Header bar ── */}
        <Box className="upd__topbar">
          <span className="upd__topbar-label">{translate('ACCOUNT')}</span>
          <IconButton
            aria-label="Close menu"
            onClick={handleClose}
            className="upd__topbar-close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Hero ── */}
        <Box className="upd__hero" aria-hidden="true">
          <div className="upd__hero-bg-arc" />
          <div className="upd__hero-body">
            <OnlineBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
            >
              <Avatar className="upd__avatar">
                {profileImageLetters || 'U'}
              </Avatar>
            </OnlineBadge>
            <div className="upd__hero-info">
              <Typography className="upd__name" component="div">
                {userInfo?.displayName}
              </Typography>
              <Typography className="upd__email" component="div">
                {userInfo?.email}
              </Typography>
              <span className="upd__status-chip">
                <VerifiedUserOutlinedIcon fontSize="inherit" />
                Online
              </span>
            </div>
          </div>
        </Box>

        {/* ── Nav section ── */}
        <Box component="nav" className="upd__nav" aria-label="Profile navigation">
          <p className="upd__nav-label">{translate('ACCOUNT')}</p>

          <Link
            to={`/${name}/profile`}
            onClick={handleClose}
            className="upd__nav-item"
          >
            <span className="upd__nav-icon">
              <PersonOutlineIcon fontSize="small" />
            </span>
            <span className="upd__nav-label-text">{translate('PROFILE')}</span>
            <ChevronRightIcon fontSize="small" className="upd__nav-chevron" />
          </Link>

          <Link
            to={`/${name}/documents`}
            onClick={handleClose}
            className="upd__nav-item"
          >
            <span className="upd__nav-icon">
              <FileCopyOutlinedIcon fontSize="small" />
            </span>
            <span className="upd__nav-label-text">{translate('DOCUMENTS')}</span>
            <ChevronRightIcon fontSize="small" className="upd__nav-chevron" />
          </Link>
        </Box>

        {/* ── Spacer ── */}
        <Box className="upd__spacer" />

        {/* ── Footer / logout ── */}
        <Box className="upd__footer">
          <button
            type="button"
            onClick={signOut}
            className="upd__logout"
          >
            <PowerSettingsNewIcon fontSize="small" />
            <span>{translate('LOGOUT')}</span>
          </button>
        </Box>

      </Box>
    </Drawer>
  );
};

export default UserProfileMobileDrawer;
